import {MultiAttribution, MultiAttributionType, MultiAttributionWeights} from './types';

type AttributionSpec = {
  // Description displayed along options for attribution type
  description: string;
  icon: string;
  label: string;
  // Values specified here: https://www.notion.so/mxpnl/Attribution-Mechanics-4d71a5dfca2e465687aecaa3cbce4363
  weights?: MultiAttributionWeights;
};

export const CustomDefaultWeights = Object.freeze({first: 33, middle: 34, last: 33});

export const AttributionTypeToSpec: Record<MultiAttributionType, AttributionSpec> = {
  [MultiAttributionType.FirstTouch]: {
    description: `Gives 100% credit to the first touchpoint within the attribution lookback window.`,
    icon: `attribution-first-touch`,
    label: `First Touch`,
    weights: {
      first: 100,
      middle: 0,
      last: 0,
    },
  },
  [MultiAttributionType.LastTouch]: {
    description: `Gives 100% credit to the last touchpoint within the attribution lookback window.`,
    icon: `attribution-last-touch`,
    label: `Last Touch`,
    weights: {
      first: 0,
      middle: 0,
      last: 100,
    },
  },
  [MultiAttributionType.Linear]: {
    description: `Gives equal credit to every touchpoint seen leading up to a conversion within the attribution lookback window.`,
    icon: `attribution-linear`,
    label: `Linear`,
    weights: {
      first: 33,
      middle: 33,
      last: 33,
    },
  },
  [MultiAttributionType.Participation]: {
    description: `Gives 100% credit to every unique touchpoint seen within attribution window. The total number of conversions is inflated compared to other attribution models. For example with 5 property values, each would receive 100% credit showing 5 conversions.`,
    icon: `attribution-participation`,
    label: `Participation`,
  },
  [MultiAttributionType.UShaped]: {
    description: `Gives 40% credit to the first touchpoint, 40% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (50%, 50%). With 6 touch points the middle 4 touch points would share the 20% (40%, 5%, 5%, 5%, 5%, 40%).`,
    icon: `attribution-u-shaped`,
    label: `U-Shaped`,
    weights: {
      first: 40,
      middle: 20,
      last: 40,
    },
  },
  [MultiAttributionType.JShaped]: {
    description: `Gives 20% credit to the first touchpoint, 60% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (25%, 75%). With 6 touch points the middle 4 touch points would share the 20% (20%, 5%, 5%, 5%, 5%, 60%).`,
    icon: `attribution-j-shaped`,
    label: `J-Shaped`,
    weights: {
      first: 20,
      middle: 20,
      last: 60,
    },
  },
  [MultiAttributionType.InverseJShaped]: {
    description: `Gives 60% credit to the first touchpoint, 20% credit to the last touchpoint, and divides the remaining 20% to any touch points in between. With 2 touchpoints, the credit is normalized (75%, 25%). With 6 touch points the middle 4 touch points would share the 20% (60%, 5%, 5%, 5%, 5%, 20%).`,
    icon: `attribution-inverse-j-shaped`,
    label: `Inverse J-Shaped`,
    weights: {
      first: 60,
      middle: 20,
      last: 20,
    },
  },
  [MultiAttributionType.Custom]: {
    description: `Customize the weightage to be given to the first and last touchpoint, and all other touchpoints in between based on your use-case.`,
    icon: `attribution-custom`,
    label: `Custom`,
  },
};

export const DefaultMultiAttribution: MultiAttribution = Object.freeze({
  type: MultiAttributionType.LastTouch,
});
